import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Search, Loader2, SlidersHorizontal, Grid2x2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { usePageMedia } from '@/lib/usePageMedia';
import RigCard, { CATEGORY_LABELS } from '@/components/showcase/RigCard';
import ShowcaseCompareTable from '@/components/showcase/ShowcaseCompareTable';

const FILTERS = [
  { key: 'all', label: 'ALL' },
  { key: 'gt', label: 'GT' },
  { key: 'formula', label: 'FORMULA' },
  { key: 'rally', label: 'RALLY' },
  { key: 'drift', label: 'DRIFT' },
];

export default function Simulators() {
  const { get } = usePageMedia();
  const heroImg = get('simulators', 'hero');
  useSiteSeo('simulators', {
    title: 'Simulator Showcase — Professional Sim Racing Rigs',
    description: 'Explore Motion Reality\'s fleet of professional-grade sim racing simulators. GT, Formula, Rally and Drift rigs — each engineered for a different kind of racing.',
  });

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCompare, setShowCompare] = useState(false);

  const { data: rigs = [], isLoading } = useQuery({
    queryKey: ['showcase-rigs-all'],
    queryFn: () => base44.entities.ShowcaseRig.list('sort_order', 100),
  });

  const filtered = rigs.filter((r) => {
    const matchesFilter = filter === 'all' || r.category === filter;
    const q = search.toLowerCase();
    const matchesSearch = !q || r.name?.toLowerCase().includes(q) || r.short_description?.toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="relative overflow-hidden py-16 px-6 lg:px-10 border-b border-white/5">
        {heroImg && (
          <>
            <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0f]/70 to-[#0d0d0f]" />
          </>
        )}
        <div className="relative max-w-7xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-primary text-xs font-bold tracking-widest mb-3">
            SIMULATOR SHOWCASE
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-white font-black text-4xl md:text-6xl uppercase leading-[0.95] mb-4"
          >
            Our Fleet
          </motion.h1>
          <p className="text-white/40 text-sm md:text-base max-w-xl">
            Professional-grade simulators engineered for every discipline. Choose your weapon and book a session.
          </p>
        </div>
      </section>

      {/* Controls */}
      <section className="sticky top-[var(--header-offset)] z-20 bg-[#0d0d0f]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rigs..."
              className="w-full bg-[#111115] border border-white/10 text-white pl-9 pr-3 h-10 text-sm placeholder:text-white/20 focus:border-primary/60 outline-none"
            />
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <SlidersHorizontal className="w-3.5 h-3.5 text-white/30 mr-1" />
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 text-[10px] font-bold tracking-widest border transition-colors ${
                  filter === f.key
                    ? 'border-primary bg-primary text-white'
                    : 'border-white/10 text-white/50 hover:text-white hover:border-white/30'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowCompare((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold tracking-widest border transition-colors ${
              showCompare ? 'border-primary bg-primary text-white' : 'border-white/10 text-white/50 hover:text-white hover:border-white/30'
            }`}
          >
            <Grid2x2 className="w-3.5 h-3.5" /> COMPARE
          </button>
        </div>
      </section>

      {/* Grid / Compare */}
      <section className="py-12 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : showCompare ? (
            <ShowcaseCompareTable />
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 border border-white/5 bg-[#111115]">
              <p className="text-white/30 text-sm">No simulators match your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((rig, i) => (
                <motion.div
                  key={rig.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.05, 0.4) }}
                >
                  <RigCard rig={rig} index={i} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Compare table at bottom (always available) */}
      {!showCompare && rigs.length > 1 && (
        <section className="py-16 px-6 lg:px-10 border-t border-white/5 bg-[#111115]">
          <div className="max-w-7xl mx-auto">
            <p className="text-primary text-xs font-bold tracking-widest mb-3">COMPARE OUR RIGS</p>
            <h2 className="text-white font-black text-3xl md:text-4xl uppercase mb-8">Side By Side</h2>
            <ShowcaseCompareTable />
          </div>
        </section>
      )}
    </div>
  );
}